import { useState, useRef } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Upload, AlertTriangle, XCircle, CheckCircle, Loader2 } from "lucide-react";
import * as XLSX from 'xlsx';

interface LinhaImportacao {
  cnpj_cpf_destinatario: string;
  razao_social: string;
  codigo_produto: string;
  descricao_produto: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
  numero_pedido: string;
  data_pedido: string;
  valor_pedido: number;
  tipo_frete?: string;
  transportadora?: string;
  anotacoes?: string;
}

interface LinhaValidada extends LinhaImportacao {
  produtoId: number | null;
  destinatarioId: number | null;
  erros: string[];
  alertas: string[];
}

interface PedidoAgrupado {
  numeroPedido: string;
  dataPedido: string;
  valorPedido: number;
  cnpjCpf: string;
  razaoSocial: string;
  tipoFrete?: string;
  transportadora?: string;
  anotacoes?: string;
  itens: Array<{
    codigoProduto: string;
    descricaoProduto: string;
    quantidade: number;
    valorUnitario: number;
    valorTotal: number;
    produtoId: number;
  }>;
  destinatarioId: number | null;
}

export default function ImportarPedidos() {
  const { user } = useGoogleAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [linhasValidadas, setLinhasValidadas] = useState<LinhaValidada[]>([]);
  const [etapa, setEtapa] = useState<'upload' | 'validacao' | 'importando' | 'concluido'>('upload');
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<{ importados: number; erros: string[] } | null>(null);

  const { data: productsData = [] } = trpc.products.list.useQuery();
  const { data: destinatariosResp } = trpc.destinatarios.list.useQuery(
    user?.role === 'cliente' && user?.emitenteId ? { emitenteId: user.emitenteId } : undefined,
    { enabled: !!user }
  );
  const destinatariosList: any[] = destinatariosResp?.data ?? [];

  const importarMutation = trpc.pedidos.importarHistorico.useMutation({
    onSuccess: (data) => {
      setResultado(data);
      setEtapa('concluido');
      setImportando(false);
      if (data.importados > 0) {
        toast.success(`${data.importados} pedido(s) importado(s) com sucesso!`);
      }
      if (data.erros.length > 0) {
        toast.error(`${data.erros.length} erro(s) durante a importação.`);
      }
    },
    onError: (err) => {
      toast.error(`Erro na importação: ${err.message}`);
      setImportando(false);
    },
  });

  // Normalizar CNPJ/CPF removendo pontuação
  const normalizarCnpjCpf = (valor: string): string => {
    return (valor || '').replace(/[.\-\/\s]/g, '');
  };

  // Download do modelo Excel
  const handleDownloadModelo = () => {
    const headers = [
      { cnpj_cpf_destinatario: '', razao_social: '', codigo_produto: '', descricao_produto: '',
        quantidade: '', valor_unitario: '', valor_total: '', numero_pedido: '',
        data_pedido: '', valor_pedido: '', tipo_frete: '', transportadora: '', anotacoes: '' }
    ];
    const worksheet = XLSX.utils.json_to_sheet(headers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');
    XLSX.writeFile(workbook, 'modelo_importacao_pedidos.xlsx');
  };

  // Upload e parse do Excel
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as LinhaImportacao[];

        if (jsonData.length === 0) {
          toast.error('Planilha vazia.');
          return;
        }

        // Validar cada linha
        const validadas: LinhaValidada[] = jsonData.map((linha) => {
          const erros: string[] = [];
          const alertas: string[] = [];

          // Campos obrigatórios
          if (!linha.cnpj_cpf_destinatario) erros.push('CNPJ/CPF obrigatório');
          if (!linha.codigo_produto) erros.push('Código do produto obrigatório');
          if (!linha.numero_pedido) erros.push('Número do pedido obrigatório');
          if (!linha.quantidade || linha.quantidade <= 0) erros.push('Quantidade inválida');
          if (!linha.valor_unitario || linha.valor_unitario < 0) erros.push('Valor unitário inválido');
          if (!linha.valor_total || linha.valor_total < 0) erros.push('Valor total inválido');
          if (!linha.data_pedido) erros.push('Data do pedido obrigatória');
          if (!linha.valor_pedido || linha.valor_pedido < 0) erros.push('Valor do pedido inválido');

          // Buscar produto por código
          const cnpjNorm = normalizarCnpjCpf(String(linha.cnpj_cpf_destinatario || ''));
          const codProduto = String(linha.codigo_produto || '').trim();
          const produto = (productsData as any[]).find((p: any) => p.cod === codProduto);
          let produtoId: number | null = null;
          if (produto) {
            produtoId = produto.id;
          } else if (codProduto) {
            erros.push(`Produto "${codProduto}" não encontrado`);
          }

          // Buscar destinatário por CNPJ/CPF
          let destinatarioId: number | null = null;
          if (cnpjNorm) {
            const dest = destinatariosList.find((d: any) => normalizarCnpjCpf(d.cnpj || '') === cnpjNorm);
            if (dest) {
              destinatarioId = dest.id;
            } else {
              alertas.push('Destinatário não encontrado — será criado');
            }
          }

          return {
            ...linha,
            produtoId,
            destinatarioId,
            erros,
            alertas,
          };
        });

        setLinhasValidadas(validadas);
        setEtapa('validacao');
      } catch (err: any) {
        toast.error(`Erro ao ler arquivo: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);
    // Resetar input para permitir re-upload do mesmo arquivo
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Agrupar linhas por numero_pedido para enviar ao backend
  const agruparPedidos = (): PedidoAgrupado[] => {
    const mapa = new Map<string, PedidoAgrupado>();

    for (const linha of linhasValidadas) {
      const key = String(linha.numero_pedido);
      if (!mapa.has(key)) {
        mapa.set(key, {
          numeroPedido: key,
          dataPedido: String(linha.data_pedido),
          valorPedido: Number(linha.valor_pedido) || 0,
          cnpjCpf: normalizarCnpjCpf(String(linha.cnpj_cpf_destinatario || '')),
          razaoSocial: String(linha.razao_social || ''),
          tipoFrete: linha.tipo_frete ? String(linha.tipo_frete) : undefined,
          transportadora: linha.transportadora ? String(linha.transportadora) : undefined,
          anotacoes: linha.anotacoes ? String(linha.anotacoes) : undefined,
          itens: [],
          destinatarioId: linha.destinatarioId,
        });
      }
      const pedido = mapa.get(key)!;
      pedido.itens.push({
        codigoProduto: String(linha.codigo_produto),
        descricaoProduto: String(linha.descricao_produto || ''),
        quantidade: Number(linha.quantidade),
        valorUnitario: Number(linha.valor_unitario),
        valorTotal: Number(linha.valor_total),
        produtoId: linha.produtoId!,
      });
    }

    return Array.from(mapa.values());
  };

  // Confirmar importação
  const handleConfirmar = () => {
    const pedidosAgrupados = agruparPedidos();
    setImportando(true);
    importarMutation.mutate({ pedidos: pedidosAgrupados });
  };

  // Estatísticas
  const totalLinhas = linhasValidadas.length;
  const linhasComErro = linhasValidadas.filter(l => l.erros.length > 0).length;
  const linhasComAlerta = linhasValidadas.filter(l => l.alertas.length > 0 && l.erros.length === 0).length;
  const linhasOk = totalLinhas - linhasComErro - linhasComAlerta;
  const pedidosUnicos = new Set(linhasValidadas.map(l => l.numero_pedido)).size;
  const temErrosBloqueantes = linhasComErro > 0;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Importar Pedidos Históricos</h1>

      {/* ETAPA: UPLOAD */}
      {etapa === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Upload da Planilha</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Faça o download do modelo, preencha com os dados dos pedidos históricos e envie o arquivo preenchido.
            </p>
            <div className="flex gap-4">
              <Button variant="outline" onClick={handleDownloadModelo}>
                <Download className="w-4 h-4 mr-2" />
                Download Modelo
              </Button>
              <Button onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                Enviar Planilha
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* ETAPA: VALIDAÇÃO */}
      {etapa === 'validacao' && (
        <>
          {/* Resumo */}
          <Card>
            <CardHeader>
              <CardTitle>Resumo da Validação</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">{totalLinhas}</div>
                  <div className="text-sm text-muted-foreground">Linhas</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{pedidosUnicos}</div>
                  <div className="text-sm text-muted-foreground">Pedidos</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{linhasOk}</div>
                  <div className="text-sm text-muted-foreground">OK</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{linhasComAlerta}</div>
                  <div className="text-sm text-muted-foreground">Alertas</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">{linhasComErro}</div>
                  <div className="text-sm text-muted-foreground">Erros</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabela de amostragem */}
          <Card>
            <CardHeader>
              <CardTitle>Dados Importados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8">#</TableHead>
                      <TableHead>Pedido</TableHead>
                      <TableHead>CNPJ/CPF</TableHead>
                      <TableHead>Razão Social</TableHead>
                      <TableHead>Produto</TableHead>
                      <TableHead>Qtd</TableHead>
                      <TableHead>Vlr Unit</TableHead>
                      <TableHead>Vlr Total</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linhasValidadas.map((linha, idx) => {
                      const temErro = linha.erros.length > 0;
                      const temAlerta = linha.alertas.length > 0;
                      const bgClass = temErro ? 'bg-red-50' : temAlerta ? 'bg-yellow-50' : '';
                      return (
                        <TableRow key={idx} className={bgClass}>
                          <TableCell className="text-xs">{idx + 1}</TableCell>
                          <TableCell className="text-xs font-mono">{linha.numero_pedido}</TableCell>
                          <TableCell className="text-xs font-mono">{linha.cnpj_cpf_destinatario}</TableCell>
                          <TableCell className="text-xs">{linha.razao_social}</TableCell>
                          <TableCell className="text-xs font-mono">{linha.codigo_produto}</TableCell>
                          <TableCell className="text-xs">{Math.floor(Number(linha.quantidade))}</TableCell>
                          <TableCell className="text-xs">{Number(linha.valor_unitario).toFixed(2)}</TableCell>
                          <TableCell className="text-xs">{Number(linha.valor_total).toFixed(2)}</TableCell>
                          <TableCell className="text-xs">
                            {temErro && (
                              <div className="flex items-center gap-1 text-red-600">
                                <XCircle className="w-3 h-3" />
                                <span>{linha.erros.join('; ')}</span>
                              </div>
                            )}
                            {!temErro && temAlerta && (
                              <div className="flex items-center gap-1 text-yellow-600">
                                <AlertTriangle className="w-3 h-3" />
                                <span>{linha.alertas.join('; ')}</span>
                              </div>
                            )}
                            {!temErro && !temAlerta && (
                              <div className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-3 h-3" />
                                <span>OK</span>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Ações */}
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => { setEtapa('upload'); setLinhasValidadas([]); }}>
              Voltar
            </Button>
            <Button
              onClick={handleConfirmar}
              disabled={temErrosBloqueantes || importando}
            >
              {importando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Importação ({pedidosUnicos} pedidos)
                </>
              )}
            </Button>
            {temErrosBloqueantes && (
              <span className="text-sm text-red-600 self-center">
                Corrija os erros na planilha antes de importar.
              </span>
            )}
          </div>
        </>
      )}

      {/* ETAPA: CONCLUÍDO */}
      {etapa === 'concluido' && resultado && (
        <Card>
          <CardHeader>
            <CardTitle>Resultado da Importação</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-lg">
              <span className="font-bold text-green-600">{resultado.importados}</span> pedido(s) importado(s) com sucesso.
            </div>
            {resultado.erros.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-semibold text-red-600">Erros ({resultado.erros.length}):</div>
                <div className="max-h-[200px] overflow-y-auto bg-red-50 p-3 rounded text-xs">
                  {resultado.erros.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </div>
              </div>
            )}
            <Button variant="outline" onClick={() => { setEtapa('upload'); setLinhasValidadas([]); setResultado(null); }}>
              Nova Importação
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
